import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Empty,
  IconButton,
  Input,
  PageHeader,
  Select,
  type SelectOption,
} from '../../components';
import { apiErrorMessage, apiRequest } from '../../lib/api';
import { usePermissions } from '../../lib/permissions';
import type { ListResponse } from '../../lib/useCrud';
import type { UserOption } from '../leads/types';
import { EventFormModal } from './EventFormModal';
import type {
  EntityLookup,
  EventCreatePayload,
  EventRow,
  EventUpdatePayload,
} from './types';
import './calendar.css';

type ViewMode = 'month' | 'agenda';

const VIEW_OPTIONS: Array<{ value: ViewMode; label: string }> = [
  { value: 'month', label: 'Month' },
  { value: 'agenda', label: 'Agenda' },
];

// Monday-first weekday order, in line with the European context of the product.
const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function monthLabel(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function relativeDayLabel(d: Date): string {
  const today = startOfDay(new Date());
  const diff = Math.round((startOfDay(d).getTime() - today.getTime()) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return '';
}

function buildMonthGrid(anchor: Date): Date[] {
  // Start of the calendar grid: walk back from the 1st until we hit Monday.
  const first = startOfMonth(anchor);
  // JS getDay(): 0=Sun..6=Sat. Convert so Monday=0..Sunday=6.
  const lead = (first.getDay() + 6) % 7;
  const gridStart = addDays(first, -lead);
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

function parseLocalYmd(ymd: string): Date | null {
  // Avoid `new Date("yyyy-mm-dd")` which is parsed as UTC midnight; build a
  // local-time date so day boundaries line up with the user's timezone.
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map((s) => parseInt(s, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

function fmtTime(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function fmtAgendaDate(d: Date): string {
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

const DEFAULT_PAGE_SIZE = 100;

export function CalendarPage() {
  const qc = useQueryClient();
  // Read-only users can browse the calendar but can't create/edit; deletes are
  // admin-only. The backend enforces this — gating here just hides dead actions.
  const { canWrite, isAdmin } = usePermissions();
  const [view, setView] = useState<ViewMode>('month');
  const [anchor, setAnchor] = useState<Date>(() => startOfMonth(new Date()));

  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [agendaFromDate, setAgendaFromDate] = useState('');
  const [agendaToDate, setAgendaToDate] = useState('');

  const [editing, setEditing] = useState<EventRow | { defaultDateISO?: string } | null>(null);

  // Date window: month view uses the visible 6-week grid; agenda view honors
  // the user-supplied date range, falling back to "now → +90 days".
  const dateWindow = useMemo(() => {
    if (view === 'month') {
      const grid = buildMonthGrid(anchor);
      return {
        from: startOfDay(grid[0]).toISOString(),
        to: endOfDay(grid[grid.length - 1]).toISOString(),
      };
    }
    const fromDate = parseLocalYmd(agendaFromDate);
    const toDate = parseLocalYmd(agendaToDate);
    const from = fromDate
      ? startOfDay(fromDate).toISOString()
      : startOfDay(new Date()).toISOString();
    const to = toDate
      ? endOfDay(toDate).toISOString()
      : endOfDay(addDays(new Date(), 90)).toISOString();
    return { from, to };
  }, [view, anchor, agendaFromDate, agendaToDate]);

  const listQuery = useMemo(() => {
    const p = new URLSearchParams();
    p.set('limit', String(DEFAULT_PAGE_SIZE));
    p.set('sort', 'start_at');
    p.set('dir', 'asc');
    p.set('from', dateWindow.from);
    p.set('to', dateWindow.to);
    if (eventTypeFilter.trim()) p.set('event_type', eventTypeFilter.trim());
    if (ownerFilter) p.set('user_id', ownerFilter);
    return p.toString();
  }, [dateWindow.from, dateWindow.to, eventTypeFilter, ownerFilter]);

  const events = useQuery({
    queryKey: ['events', listQuery],
    queryFn: () => apiRequest<ListResponse<EventRow>>(`/events?${listQuery}`),
  });

  // Lookups for filter / form selectors. Cached separately from the event list.
  const usersQ = useQuery({
    queryKey: ['lookup', 'users'],
    queryFn: () =>
      apiRequest<ListResponse<UserOption>>('/users?limit=200&include_inactive=false'),
  });
  const leadsQ = useQuery({
    queryKey: ['lookup', 'leads-min'],
    queryFn: () => apiRequest<ListResponse<EntityLookup>>('/leads?limit=100&sort=full_name&dir=asc'),
  });
  const clientsQ = useQuery({
    queryKey: ['lookup', 'clients-min'],
    queryFn: () =>
      apiRequest<ListResponse<EntityLookup>>('/clients?limit=100&sort=full_name&dir=asc'),
  });
  const contactsQ = useQuery({
    queryKey: ['lookup', 'contacts-min'],
    queryFn: () =>
      apiRequest<ListResponse<EntityLookup>>('/contacts?limit=100&sort=full_name&dir=asc'),
  });

  const users = usersQ.data?.data ?? [];
  const leads = leadsQ.data?.data ?? [];
  const clients = clientsQ.data?.data ?? [];
  const contacts = contactsQ.data?.data ?? [];

  const userById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const leadById = useMemo(() => new Map(leads.map((l) => [l.id, l])), [leads]);
  const clientById = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);
  const contactById = useMemo(() => new Map(contacts.map((c) => [c.id, c])), [contacts]);

  // Derive an event-type filter dropdown from currently visible events. The backend
  // doesn't have a settings table for event types — they're free-form strings.
  const eventTypeOpts: SelectOption[] = useMemo(() => {
    const set = new Set<string>();
    for (const ev of events.data?.data ?? []) if (ev.event_type) set.add(ev.event_type);
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((t) => ({ value: t, label: t }));
  }, [events.data]);

  const ownerOpts: SelectOption[] = users.map((u) => ({ value: u.id, label: u.full_name }));

  const invalidate = () => qc.invalidateQueries({ queryKey: ['events'] });

  const createMut = useMutation({
    mutationFn: (body: EventCreatePayload) =>
      apiRequest<{ data: EventRow }>('/events', { method: 'POST', body }),
    onSuccess: invalidate,
  });
  const updateMut = useMutation({
    mutationFn: (args: { id: string; body: EventUpdatePayload }) =>
      apiRequest<{ data: EventRow }>(`/events/${args.id}`, {
        method: 'PATCH',
        body: args.body,
      }),
    onSuccess: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/events/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  // Group events by local-date key so each calendar cell / agenda day section can read its bucket.
  const eventsByDate = useMemo(() => {
    const map = new Map<string, EventRow[]>();
    for (const ev of events.data?.data ?? []) {
      const key = dateKey(new Date(ev.start_at));
      const arr = map.get(key) ?? [];
      arr.push(ev);
      map.set(key, arr);
    }
    return map;
  }, [events.data]);

  function gotoToday() {
    setAnchor(startOfMonth(new Date()));
  }
  function gotoPrev() {
    setAnchor((a) => new Date(a.getFullYear(), a.getMonth() - 1, 1));
  }
  function gotoNext() {
    setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + 1, 1));
  }

  const editingRow: EventRow | undefined =
    editing && 'id' in editing ? (editing as EventRow) : undefined;
  const editingDefault: string | undefined =
    editing && !('id' in editing) ? (editing as { defaultDateISO?: string }).defaultDateISO : undefined;

  return (
    <>
      <PageHeader
        title="Calendar"
        subtitle="Meetings, calls, demos, and other scheduled events."
        actions={
          canWrite ? (
            <Button variant="primary" icon="plus" onClick={() => setEditing({})}>
              New event
            </Button>
          ) : null
        }
      />

      <div className="card" style={{ marginBottom: 12 }}>
        <div
          className="card-header cal-toolbar"
          style={{ borderBottom: 'none', padding: '12px 16px' }}
        >
          {view === 'month' ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <IconButton name="chevronLeft" label="Previous month" onClick={gotoPrev} />
                <IconButton name="chevronRight" label="Next month" onClick={gotoNext} />
              </div>
              <div className="cal-title">{monthLabel(anchor)}</div>
              <Button size="sm" onClick={gotoToday}>
                Today
              </Button>
            </>
          ) : (
            <>
              <div className="cal-title">Agenda</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Input
                  type="date"
                  value={agendaFromDate}
                  onChange={setAgendaFromDate}
                  aria-label="From date"
                />
                <span className="muted" style={{ fontSize: 12 }}>
                  →
                </span>
                <Input
                  type="date"
                  value={agendaToDate}
                  onChange={setAgendaToDate}
                  aria-label="To date"
                />
                {(agendaFromDate || agendaToDate) && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setAgendaFromDate('');
                      setAgendaToDate('');
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </>
          )}

          <div style={{ flex: 1 }} />

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Select
              placeholder="All types"
              value={eventTypeFilter}
              options={eventTypeOpts}
              onChange={setEventTypeFilter}
              aria-label="Event type filter"
            />
            <Select
              placeholder="Any owner"
              value={ownerFilter}
              options={ownerOpts}
              onChange={setOwnerFilter}
              aria-label="Owner filter"
            />
            <div className="cal-view-toggle" role="tablist">
              {VIEW_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  role="tab"
                  aria-selected={view === o.value}
                  className={view === o.value ? 'active' : ''}
                  onClick={() => setView(o.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {events.error && (
        <div className="alert">
          {events.error instanceof Error ? events.error.message : 'Failed to load events'}
        </div>
      )}

      {view === 'month' ? (
        <MonthView
          anchor={anchor}
          eventsByDate={eventsByDate}
          loading={events.isLoading}
          onCreateAt={canWrite ? (d) => setEditing({ defaultDateISO: d }) : undefined}
          onOpenEvent={(ev) => setEditing(ev)}
        />
      ) : (
        <AgendaView
          rows={events.data?.data ?? []}
          loading={events.isLoading}
          canWrite={canWrite}
          userById={userById}
          leadById={leadById}
          clientById={clientById}
          contactById={contactById}
          onOpenEvent={(ev) => setEditing(ev)}
        />
      )}

      {editing && (
        <EventFormModal
          row={editingRow}
          defaultDateISO={editingDefault}
          canWrite={canWrite}
          users={users}
          leads={leads}
          clients={clients}
          contacts={contacts}
          onClose={() => setEditing(null)}
          onSubmit={async (payload) => {
            if (editingRow) {
              await updateMut.mutateAsync({ id: editingRow.id, body: payload as EventUpdatePayload });
            } else {
              await createMut.mutateAsync(payload as EventCreatePayload);
            }
            setEditing(null);
          }}
          onDelete={
            editingRow && isAdmin
              ? async () => {
                  try {
                    await deleteMut.mutateAsync(editingRow.id);
                    setEditing(null);
                  } catch (err) {
                    alert(apiErrorMessage(err, 'Delete failed'));
                  }
                }
              : undefined
          }
        />
      )}
    </>
  );
}

function MonthView({
  anchor,
  eventsByDate,
  loading,
  onCreateAt,
  onOpenEvent,
}: {
  anchor: Date;
  eventsByDate: Map<string, EventRow[]>;
  loading: boolean;
  /** Omitted for read-only users — clicking a day then does nothing. */
  onCreateAt?: (dateISO: string) => void;
  onOpenEvent: (ev: EventRow) => void;
}) {
  const grid = useMemo(() => buildMonthGrid(anchor), [anchor]);
  const today = new Date();
  const month = anchor.getMonth();

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div className="cal-grid">
        {DOW_LABELS.map((d) => (
          <div key={d} className="cal-dow">
            {d}
          </div>
        ))}
        {grid.map((d) => {
          const key = dateKey(d);
          const dayEvents = eventsByDate.get(key) ?? [];
          dayEvents.sort((a, b) => a.start_at.localeCompare(b.start_at));
          const visible = dayEvents.slice(0, 3);
          const moreCount = dayEvents.length - visible.length;
          const isOther = d.getMonth() !== month;
          const isToday = sameDay(d, today);
          return (
            <div
              key={key}
              className={`cal-day ${isOther ? 'other-month' : ''} ${isToday ? 'is-today' : ''}`}
              onClick={onCreateAt ? () => onCreateAt(key) : undefined}
            >
              <div className="cal-day-head">
                <span className="cal-day-num">{d.getDate()}</span>
                {onCreateAt && <span className="cal-day-add">+</span>}
              </div>
              {visible.map((ev) => (
                <div
                  key={ev.id}
                  className={`cal-event ${ev.is_all_day ? 'allday' : ''}`}
                  title={ev.title}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenEvent(ev);
                  }}
                >
                  {!ev.is_all_day && (
                    <span className="cal-event-time">{fmtTime(new Date(ev.start_at))}</span>
                  )}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.title}</span>
                </div>
              ))}
              {moreCount > 0 && <div className="cal-day-more">+{moreCount} more</div>}
            </div>
          );
        })}
      </div>
      {loading && (
        <div className="card-body muted" style={{ padding: 12, textAlign: 'center' }}>
          Loading…
        </div>
      )}
    </div>
  );
}

function AgendaView({
  rows,
  loading,
  canWrite,
  userById,
  leadById,
  clientById,
  contactById,
  onOpenEvent,
}: {
  rows: EventRow[];
  loading: boolean;
  canWrite: boolean;
  userById: Map<string, UserOption>;
  leadById: Map<string, EntityLookup>;
  clientById: Map<string, EntityLookup>;
  contactById: Map<string, EntityLookup>;
  onOpenEvent: (ev: EventRow) => void;
}) {
  const groups = useMemo(() => {
    const map = new Map<string, EventRow[]>();
    const sorted = [...rows].sort((a, b) => a.start_at.localeCompare(b.start_at));
    for (const ev of sorted) {
      const key = dateKey(new Date(ev.start_at));
      const arr = map.get(key) ?? [];
      arr.push(ev);
      map.set(key, arr);
    }
    return Array.from(map.entries());
  }, [rows]);

  if (loading) {
    return <div className="card card-body muted">Loading…</div>;
  }
  if (groups.length === 0) {
    return (
      <div className="card">
        <Empty
          icon="calendar"
          title="No events"
          subtitle="Use “New event” to schedule meetings, calls, or demos."
        />
      </div>
    );
  }

  function metaFor(ev: EventRow) {
    const parts: Array<{ key: string; node: ReactNode }> = [];
    if (ev.event_type) {
      parts.push({
        key: 'type',
        node: <span className="badge gray">{ev.event_type}</span>,
      });
    }
    const owner = ev.owner ?? (ev.user_id ? userById.get(ev.user_id) ?? null : null);
    if (owner) {
      parts.push({
        key: 'owner',
        node: <span>{owner.full_name}</span>,
      });
    }
    if (ev.lead_id) {
      const lead = leadById.get(ev.lead_id);
      parts.push({
        key: 'lead',
        node: (
          <Link to={`/leads/${ev.lead_id}`} onClick={(e) => e.stopPropagation()}>
            <span className="badge blue">Lead: {lead?.full_name ?? '—'}</span>
          </Link>
        ),
      });
    }
    if (ev.client_id) {
      const client = clientById.get(ev.client_id);
      parts.push({
        key: 'client',
        node: (
          <Link to={`/clients/${ev.client_id}`} onClick={(e) => e.stopPropagation()}>
            <span className="badge violet">Client: {client?.full_name ?? '—'}</span>
          </Link>
        ),
      });
    }
    if (ev.contact_id) {
      const contact = contactById.get(ev.contact_id);
      parts.push({
        key: 'contact',
        node: <span className="badge amber">Contact: {contact?.full_name ?? '—'}</span>,
      });
    }
    return parts;
  }

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      {groups.map(([key, dayEvents]) => {
        const date = new Date(`${key}T00:00:00`);
        const rel = relativeDayLabel(date);
        return (
          <div key={key} className="agenda-day">
            <div className="agenda-day-head">
              <span className="agenda-day-date">{fmtAgendaDate(date)}</span>
              {rel && <span className="agenda-day-rel">{rel}</span>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {dayEvents.map((ev) => {
                const start = new Date(ev.start_at);
                const end = ev.end_at ? new Date(ev.end_at) : null;
                return (
                  <div
                    key={ev.id}
                    className="agenda-row"
                    onClick={() => onOpenEvent(ev)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpenEvent(ev);
                      }
                    }}
                  >
                    <div className="agenda-time">
                      {ev.is_all_day ? (
                        <span className="allday-pill">All day</span>
                      ) : (
                        <>
                          {fmtTime(start)}
                          {end && !sameDay(start, end) ? '' : end ? ` – ${fmtTime(end)}` : ''}
                        </>
                      )}
                    </div>
                    <div>
                      <div className="agenda-title">{ev.title}</div>
                      <div className="agenda-meta">
                        {metaFor(ev).map((p) => (
                          <span key={p.key}>{p.node}</span>
                        ))}
                      </div>
                    </div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      {canWrite ? 'Edit' : 'View'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

